import React, { useEffect, useState } from 'react';
import { TopNav } from './TopNav';
import { OrdersOverview } from './OrdersOverview';
import { MasterDataManagement } from './MasterDataManagement';
import { GlobalSettings } from './GlobalSettings';
import { AdminAuthModal } from './AdminAuthModal';
import { ChevronDown, QrCode, Play, CheckCircle, ArrowLeft, Search, Thermometer, Scale, X, Scan } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Order } from '../types/Order';
type MainScreenState = 'scan' | 'confirm' | 'selectLine' | 'checkProduct' | 'measureTemp' | 'measureWeight' | 'removeItem' | 'processing' | 'orders' | 'masterData';
interface PruflingRecord {
  id: number;
  temp: number;
  weight: number;
  timestamp: string;
}
interface MainScreenProps {
  onLogout: () => void;
}
export function MainScreen({
  onLogout
}: MainScreenProps) {
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [screenState, setScreenState] = useState<MainScreenState>('scan');
  const [selectedLine, setSelectedLine] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTemp, setCurrentTemp] = useState(0);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [weighedCount, setWeighedCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [pruflingHistory, setPruflingHistory] = useState<PruflingRecord[]>([]);
  const [capturedTemp, setCapturedTemp] = useState(0);
  const [tempStabilized, setTempStabilized] = useState(false);
  const [showTempWarning, setShowTempWarning] = useState(false);
  const [showRFIDAuth, setShowRFIDAuth] = useState(false);
  const [rfidAuthSuccess, setRFIDAuthSuccess] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [adminAuthTarget, setAdminAuthTarget] = useState<'settings' | 'masterData' | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsNavVisible(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  // Simuliere Temperatur/Gewichtsschwankungen
  useEffect(() => {
    if (screenState === 'measureTemp') {
      // Nach 5 Sekunden stabilisiert sich die Temperatur bei 5-7 Grad
      const stabilizeTimer = setTimeout(() => {
        setTempStabilized(true);
      }, 5000);
      const interval = setInterval(() => {
        if (tempStabilized) {
          // Stabilisierte Temperatur zwischen 5-7 Grad
          setCurrentTemp(5 + Math.random() * 2);
        } else {
          // Anfangs schwankende Temperatur
          setCurrentTemp(20 + Math.random() * 2);
        }
      }, 500);
      return () => {
        clearInterval(interval);
        clearTimeout(stabilizeTimer);
      };
    } else {
      setTempStabilized(false);
    }
    if (screenState === 'measureWeight') {
      const interval = setInterval(() => {
        setCurrentWeight(2.45 + Math.random() * 0.1);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [screenState, tempStabilized]);
  const showNav = () => {
    setIsNavVisible(true);
    setTimeout(() => {
      setIsNavVisible(false);
    }, 3000);
  };
  const handleDemo = () => {
    setScreenState('confirm');
  };
  const handleConfirm = () => {
    setScreenState('selectLine');
    setSearchQuery('');
    setSelectedLine('');
  };
  const handleLineConfirm = () => {
    if (selectedLine) {
      setScreenState('checkProduct');
    }
  };
  const handleStart = () => {
    setScreenState('measureTemp');
    setWeighedCount(0);
    setPruflingHistory([]);
    setCurrentTemp(21);
    setTempStabilized(false);
  };
  const handleTempConfirm = () => {
    // Prüfe ob Temperatur im gültigen Bereich (5-7°C)
    if (currentTemp < 5 || currentTemp > 7) {
      setShowTempWarning(true);
    } else {
      setCapturedTemp(currentTemp);
      setScreenState('measureWeight');
      setCurrentWeight(2.48);
    }
  };
  const handleTempWarningQuittieren = () => {
    setShowTempWarning(false);
    setShowRFIDAuth(true);
    setRFIDAuthSuccess(false);
  };
  const handleTempWarningZuruck = () => {
    setShowTempWarning(false);
  };
  const handleRFIDAuthDemo = () => {
    setRFIDAuthSuccess(true);
    setTimeout(() => {
      setShowRFIDAuth(false);
      setRFIDAuthSuccess(false);
      setCapturedTemp(currentTemp);
      setScreenState('measureWeight');
      setCurrentWeight(2.48);
    }, 2000);
  };
  const handleWeightCapture = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const newRecord: PruflingRecord = {
      id: weighedCount + 1,
      temp: capturedTemp,
      weight: currentWeight,
      timestamp: timeString
    };
    setPruflingHistory([...pruflingHistory, newRecord]);
    setScreenState('removeItem');
  };
  const handleNextItem = () => {
    const newCount = weighedCount + 1;
    setWeighedCount(newCount);
    if (newCount >= 5) {
      setScreenState('processing');
      // Nach 2 Sekunden zurück zur Startseite
      setTimeout(() => {
        setScreenState('scan');
      }, 2000);
    } else {
      setScreenState('measureTemp');
      setCurrentTemp(21);
    }
  };
  const handleBackToScan = () => {
    setScreenState('scan');
  };
  const handleBackToConfirm = () => {
    setScreenState('confirm');
  };
  const handleBackToSelectLine = () => {
    setScreenState('selectLine');
  };
  const orderData = {
    chargenNummer: 'CH-2024-001',
    produktName: 'P-123123',
    produktNr: '123123',
    sollgewicht: '2.5 kg'
  };
  const totalPruflinge = 5;
  const lines = ['Linie 1', 'Linie 2', 'Linie 3', 'Linie 4', 'Linie 5', 'Linie 6'];
  const filteredLines = lines.filter(line => line.toLowerCase().includes(searchQuery.toLowerCase()));
  const getProgress = () => {
    if (screenState === 'confirm') return 33;
    if (screenState === 'selectLine') return 66;
    if (screenState === 'checkProduct' || screenState === 'measureTemp' || screenState === 'measureWeight' || screenState === 'removeItem' || screenState === 'processing') return 100;
    return 0;
  };
  const progress = getProgress();
  const handleOrdersClick = () => {
    setScreenState('orders');
    setIsNavVisible(true);
  };
  const handleMasterDataClick = () => {
    setAdminAuthTarget('masterData');
    setShowAdminAuth(true);
  };
  const handleSettingsClick = () => {
    setAdminAuthTarget('settings');
    setShowAdminAuth(true);
  };
  const handleAdminAuthSuccess = () => {
    setShowAdminAuth(false);
    if (adminAuthTarget === 'settings') {
      setShowSettings(true);
      setIsNavVisible(true);
    } else if (adminAuthTarget === 'masterData') {
      setScreenState('masterData');
      setIsNavVisible(true);
    }
    setAdminAuthTarget(null);
  };
  const handleAdminAuthClose = () => {
    setShowAdminAuth(false);
    setAdminAuthTarget(null);
  };
  const handleBackFromOrders = () => {
    setScreenState('scan');
    setTimeout(() => {
      setIsNavVisible(false);
    }, 3000);
  };
  const handleBackFromMasterData = () => {
    setScreenState('scan');
    setTimeout(() => {
      setIsNavVisible(false);
    }, 3000);
  };
  const handleBackFromSettings = () => {
    setShowSettings(false);
    setTimeout(() => {
      setIsNavVisible(false);
    }, 3000);
  };
  const handleStartInspectionFromOrder = (order: Order) => {
    // Start inspection flow for existing order
    console.log('Starting inspection for order:', order.id);
    // Skip to measurement phase directly
    setScreenState('measureTemp');
    setWeighedCount(0);
    setPruflingHistory([]);
    setCurrentTemp(21);
    setTempStabilized(false);
  };
  if (screenState === 'orders') {
    return <OrdersOverview onBack={handleBackFromOrders} onStartInspection={handleStartInspectionFromOrder} data-id="element-179" />;
  }
  if (screenState === 'masterData') {
    return <MasterDataManagement onBack={handleBackFromMasterData} data-id="element-180" />;
  }
  if (showSettings) {
    return <GlobalSettings onBack={handleBackFromSettings} data-id="element-181" />;
  }
  return <div className="min-h-screen w-full bg-white" data-id="element-182">
      <TopNav isVisible={isNavVisible} userName="Max Mustermann" onOrdersClick={handleOrdersClick} onMasterDataClick={handleMasterDataClick} onSettingsClick={handleSettingsClick} onLogout={onLogout} data-id="element-183" />

      {!isNavVisible && <button onClick={showNav} className="fixed top-0 left-1/2 -translate-x-1/2 z-50 bg-white border border-gray-200 rounded-b-lg px-4 py-2 shadow-sm hover:bg-gray-50 transition-colors" aria-label="Menü anzeigen" data-id="element-184">
          <ChevronDown className="w-5 h-5 text-gray-600" strokeWidth={1.5} data-id="element-185" />
        </button>}

      {/* Temperatur Warnung Modal */}
      <AnimatePresence data-id="element-186">
        {showTempWarning && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" data-id="element-187" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-188">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.3
          }} className="w-full max-w-md" data-id="element-189">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-190">
                  <div className="bg-red-500 p-6" data-id="element-191">
                    <h3 className="text-2xl font-semibold text-white text-center" data-id="element-192">
                      Referenztemperatur unterschritten
                    </h3>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-193">
                    <div className="text-center space-y-2" data-id="element-194">
                      <div className="text-6xl" data-id="element-195">⚠️</div>
                      <p className="text-gray-600" data-id="element-196">
                        Die gemessene Temperatur liegt außerhalb des zulässigen
                        Bereichs (5-7°C)
                      </p>
                      <div className="text-2xl font-bold text-gray-900 mt-4" data-id="element-197">
                        {currentTemp.toFixed(1)} °C
                      </div>
                    </div>

                    <div className="flex flex-col gap-3" data-id="element-198">
                      <button onClick={handleTempWarningQuittieren} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm" data-id="element-199">
                        Quittieren
                      </button>
                      <button onClick={handleTempWarningZuruck} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors border border-gray-200" data-id="element-200">
                        Zurück
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* RFID Supervisor Autorisierung Modal */}
      <AnimatePresence data-id="element-201">
        {showRFIDAuth && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[80]" data-id="element-202" />
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-id="element-203">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.3
          }} className="w-full max-w-md" data-id="element-204">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-205">
                  {!rfidAuthSuccess ? <>
                      <div className="bg-orange-500 p-6" data-id="element-206">
                        <h3 className="text-2xl font-semibold text-white text-center" data-id="element-207">
                          Supervisor-Autorisierung
                        </h3>
                      </div>

                      <div className="p-8 space-y-6" data-id="element-208">
                        <div className="text-center space-y-4" data-id="element-209">
                          <div className="text-5xl mb-4" data-id="element-210">👤</div>
                          <p className="text-gray-900 text-lg font-medium" data-id="element-211">
                            Supervisor anfordern
                          </p>
                          <p className="text-gray-600" data-id="element-212">
                            Ein Supervisor muss die Quittierung mit seinem
                            RFID-Chip bestätigen
                          </p>

                          <div className="flex justify-center py-6" data-id="element-213">
                            <div className="relative" data-id="element-214">
                              <div className="absolute inset-0 bg-blue-50 rounded-3xl blur-2xl opacity-40" data-id="element-215" />
                              <div className="relative bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-200/50 shadow-lg" data-id="element-216">
                                <Scan className="w-24 h-24 text-blue-600" strokeWidth={1.5} data-id="element-217" />
                              </div>
                            </div>
                          </div>

                          <p className="text-sm text-gray-500" data-id="element-218">
                            Warten auf RFID-Chip...
                          </p>
                        </div>
                      </div>
                    </> : <>
                      <div className="bg-green-600 p-6" data-id="element-219">
                        <h3 className="text-2xl font-semibold text-white text-center" data-id="element-220">
                          Autorisiert
                        </h3>
                      </div>

                      <div className="p-8 space-y-6" data-id="element-221">
                        <div className="text-center space-y-4" data-id="element-222">
                          <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 200
                    }} data-id="element-223">
                            <CheckCircle className="w-24 h-24 text-green-600 mx-auto" strokeWidth={1.5} data-id="element-224" />
                          </motion.div>
                          <div className="space-y-2" data-id="element-225">
                            <h4 className="text-2xl font-semibold text-gray-900" data-id="element-226">
                              Supervisor: Max Mustermann
                            </h4>
                            <p className="text-gray-600" data-id="element-227">
                              Quittierung erfolgreich bestätigt
                            </p>
                          </div>
                        </div>
                      </div>
                    </>}
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Historie Modal */}
      <AnimatePresence data-id="element-228">
        {showHistory && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowHistory(false)} data-id="element-229" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none" data-id="element-230">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.3
          }} className="w-full max-w-2xl pointer-events-auto" data-id="element-231">
                <div className="bg-white rounded-2xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-232">
                  <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-233">
                    <h3 className="text-2xl font-semibold text-gray-900" data-id="element-234">
                      Erfasste Prüflinge
                    </h3>
                    <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-235">
                      <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-236" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6" data-id="element-237">
                    {pruflingHistory.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-238">
                        Noch keine Prüflinge erfasst
                      </div> : <div className="space-y-3" data-id="element-239">
                        {pruflingHistory.map(record => <div key={record.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-240">
                            <div className="flex items-center justify-between mb-3" data-id="element-241">
                              <div className="font-semibold text-gray-900" data-id="element-242">
                                Prüfling {record.id}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-243">
                                {record.timestamp}
                              </div>
                            </div>
                            <div className="flex gap-6" data-id="element-244">
                              <div className="flex items-center gap-2" data-id="element-245">
                                <Thermometer className="w-4 h-4 text-orange-500" strokeWidth={2} data-id="element-246" />
                                <span className="text-sm font-medium text-gray-700" data-id="element-247">
                                  {record.temp.toFixed(1)} °C
                                </span>
                              </div>
                              <div className="flex items-center gap-2" data-id="element-248">
                                <Scale className="w-4 h-4 text-green-600" strokeWidth={2} data-id="element-249" />
                                <span className="text-sm font-medium text-gray-700" data-id="element-250">
                                  {record.weight.toFixed(2)} kg
                                </span>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {progress > 0 && !['measureTemp', 'measureWeight', 'removeItem'].includes(screenState) && <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 w-full max-w-md px-8" data-id="element-251">
            <div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-252">
              <motion.div className="absolute top-0 left-0 h-full bg-blue-600 rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-253" />
            </div>
            <div className="flex justify-between mt-3" data-id="element-254">
              <div className={`w-2 h-2 rounded-full ${progress >= 33 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-255" />
              <div className={`w-2 h-2 rounded-full ${progress >= 66 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-256" />
              <div className={`w-2 h-2 rounded-full ${progress >= 100 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-257" />
            </div>
          </div>}

      <div className="flex items-center justify-center min-h-screen px-8" data-id="element-258">
        <AnimatePresence mode="wait" data-id="element-259">
          {screenState === 'scan' && <motion.div key="scan" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="text-center space-y-16 max-w-3xl" data-id="element-260">
              <div className="space-y-3" data-id="element-261">
                <h1 className="text-6xl font-semibold text-gray-900 tracking-tight" data-id="element-262">
                  Auftragsdaten scannen
                </h1>
                <p className="text-lg text-gray-500" data-id="element-263">
                  Scannen Sie den QR-Code des Auftrags
                </p>
              </div>

              <div className="flex justify-center py-12" data-id="element-264">
                <div className="relative" data-id="element-265">
                  <div className="absolute inset-0 bg-blue-50 rounded-3xl blur-2xl opacity-40" data-id="element-266" />
                  <div className="relative bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-12 border border-gray-200/50 shadow-lg" data-id="element-267">
                    <QrCode className="w-40 h-40 text-blue-600" strokeWidth={1.5} data-id="element-268" />
                  </div>
                </div>
              </div>
            </motion.div>}

          {screenState === 'confirm' && <motion.div key="confirm" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-2xl space-y-8" data-id="element-269">
              <div className="text-center space-y-2" data-id="element-270">
                <h2 className="text-4xl font-semibold text-gray-900" data-id="element-271">
                  Auftragsdaten prüfen
                </h2>
                <p className="text-gray-500" data-id="element-272">
                  Bitte überprüfen Sie die gescannten Daten
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 space-y-6" data-id="element-273">
                <div className="space-y-4" data-id="element-274">
                  <div className="flex justify-between items-center py-4 border-b border-gray-200" data-id="element-275">
                    <span className="text-gray-600 font-medium" data-id="element-276">
                      Chargennummer
                    </span>
                    <span className="text-gray-900 font-semibold text-lg" data-id="element-277">
                      {orderData.chargenNummer}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-4 border-b border-gray-200" data-id="element-278">
                    <span className="text-gray-600 font-medium" data-id="element-279">
                      Produktname
                    </span>
                    <span className="text-gray-900 font-semibold text-lg" data-id="element-280">
                      {orderData.produktName}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-4 border-b border-gray-200" data-id="element-281">
                    <span className="text-gray-600 font-medium" data-id="element-282">
                      Produkt-Nr.
                    </span>
                    <span className="text-gray-900 font-semibold text-lg" data-id="element-283">
                      {orderData.produktNr}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-4" data-id="element-284">
                    <span className="text-gray-600 font-medium" data-id="element-285">
                      Sollgewicht
                    </span>
                    <span className="text-gray-900 font-semibold text-lg" data-id="element-286">
                      {orderData.sollgewicht}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4" data-id="element-287">
                <button onClick={handleBackToScan} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors border border-gray-200" data-id="element-288">
                  <ArrowLeft className="w-5 h-5" strokeWidth={2} data-id="element-289" />
                  Zurück
                </button>

                <button onClick={handleConfirm} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm" data-id="element-290">
                  <CheckCircle className="w-5 h-5" strokeWidth={2} data-id="element-291" />
                  Bestätigen
                </button>
              </div>
            </motion.div>}

          {screenState === 'selectLine' && <motion.div key="selectLine" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-2xl space-y-8" data-id="element-292">
              <div className="text-center space-y-2" data-id="element-293">
                <h2 className="text-4xl font-semibold text-gray-900" data-id="element-294">
                  Linie auswählen
                </h2>
                <p className="text-gray-500" data-id="element-295">
                  Wählen Sie die Produktionslinie für diesen Auftrag
                </p>
              </div>

              <div className="space-y-4" data-id="element-296">
                <div className="relative" data-id="element-297">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={2} data-id="element-298" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Linie suchen..." className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-299" />
                </div>

                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 h-64 overflow-y-auto" data-id="element-300">
                  <div className="space-y-2" data-id="element-301">
                    {filteredLines.length > 0 ? filteredLines.map(line => <button key={line} onClick={() => setSelectedLine(line)} className={`w-full px-6 py-5 rounded-xl font-medium text-lg transition-all ${selectedLine === line ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-900 hover:bg-gray-100 border border-gray-200'}`} data-id="element-302">
                          {line}
                        </button>) : <div className="text-center py-8 text-gray-500" data-id="element-303">
                        Keine Linien gefunden
                      </div>}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4" data-id="element-304">
                <button onClick={handleBackToConfirm} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors border border-gray-200" data-id="element-305">
                  <ArrowLeft className="w-5 h-5" strokeWidth={2} data-id="element-306" />
                  Zurück
                </button>

                <button onClick={handleLineConfirm} disabled={!selectedLine} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-307">
                  <CheckCircle className="w-5 h-5" strokeWidth={2} data-id="element-308" />
                  Bestätigen
                </button>
              </div>
            </motion.div>}

          {screenState === 'checkProduct' && <motion.div key="checkProduct" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-2xl space-y-8" data-id="element-309">
              <div className="text-center space-y-2" data-id="element-310">
                <h2 className="text-4xl font-semibold text-gray-900" data-id="element-311">
                  Produktkontrolle
                </h2>
                <p className="text-gray-500" data-id="element-312">
                  Überprüfen Sie den Produktnamen vor dem Start
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-12 border border-gray-200" data-id="element-313">
                <div className="text-center space-y-4" data-id="element-314">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wide" data-id="element-315">
                    Produktname
                  </div>
                  <div className="text-5xl font-bold text-gray-900" data-id="element-316">
                    {orderData.produktName}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4" data-id="element-317">
                <button onClick={handleBackToSelectLine} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors border border-gray-200" data-id="element-318">
                  <ArrowLeft className="w-5 h-5" strokeWidth={2} data-id="element-319" />
                  Zurück
                </button>

                <button onClick={handleStart} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors shadow-sm" data-id="element-320">
                  <Play className="w-5 h-5" strokeWidth={2} data-id="element-321" />
                  Start
                </button>
              </div>
            </motion.div>}

          {screenState === 'measureTemp' && <motion.div key="measureTemp" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-xl space-y-8" data-id="element-322">
              <div className="text-center space-y-4" data-id="element-323">
                <button onClick={() => setShowHistory(true)} className="inline-block px-4 py-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors cursor-pointer" data-id="element-324">
                  <span className="text-sm font-semibold text-blue-700" data-id="element-325">
                    Prüfling {weighedCount + 1} / {totalPruflinge}
                  </span>
                </button>
                <h2 className="text-3xl font-semibold text-gray-900" data-id="element-326">
                  Kerntemperatur ermitteln
                </h2>
              </div>

              <motion.div animate={{
            background: tempStabilized ? 'linear-gradient(to bottom right, rgb(34, 197, 94), rgb(22, 163, 74))' : 'linear-gradient(to bottom right, rgb(249, 115, 22), rgb(239, 68, 68))'
          }} transition={{
            duration: 0.8
          }} className="rounded-2xl p-16 shadow-xl" data-id="element-327">
                <div className="flex items-center justify-center gap-3 mb-4" data-id="element-328">
                  <Thermometer className="w-6 h-6 text-white" strokeWidth={2} data-id="element-329" />
                  <span className="text-sm font-medium text-white/80 uppercase tracking-wide" data-id="element-330">
                    Aktuelle Temperatur
                  </span>
                </div>
                <div className="text-center" data-id="element-331">
                  <motion.div key={currentTemp} initial={{
                opacity: 0.7
              }} animate={{
                opacity: 1
              }} className="text-7xl font-bold text-white tabular-nums" data-id="element-332">
                    {currentTemp.toFixed(1)}
                  </motion.div>
                  <div className="text-3xl font-medium text-white/80 mt-2" data-id="element-333">
                    °C
                  </div>
                </div>
              </motion.div>

              <button onClick={handleTempConfirm} className="w-full flex items-center justify-center gap-2 px-6 py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm text-lg" data-id="element-334">
                <CheckCircle className="w-6 h-6" strokeWidth={2} data-id="element-335" />
                Bestätigen
              </button>
            </motion.div>}

          {screenState === 'measureWeight' && <motion.div key="measureWeight" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-xl space-y-8" data-id="element-336">
              <div className="text-center space-y-4" data-id="element-337">
                <button onClick={() => setShowHistory(true)} className="inline-block px-4 py-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors cursor-pointer" data-id="element-338">
                  <span className="text-sm font-semibold text-blue-700" data-id="element-339">
                    Prüfling {weighedCount + 1} / {totalPruflinge}
                  </span>
                </button>
                <h2 className="text-3xl font-semibold text-gray-900" data-id="element-340">
                  Gewicht erfassen
                </h2>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-16 shadow-xl" data-id="element-341">
                <div className="flex items-center justify-center gap-3 mb-4" data-id="element-342">
                  <Scale className="w-6 h-6 text-green-400" strokeWidth={2} data-id="element-343" />
                  <span className="text-sm font-medium text-gray-400 uppercase tracking-wide" data-id="element-344">
                    Aktuelles Gewicht
                  </span>
                </div>
                <div className="text-center" data-id="element-345">
                  <motion.div key={currentWeight} initial={{
                opacity: 0.7
              }} animate={{
                opacity: 1
              }} className="text-7xl font-bold text-green-400 tabular-nums" data-id="element-346">
                    {currentWeight.toFixed(2)}
                  </motion.div>
                  <div className="text-3xl font-medium text-gray-500 mt-2" data-id="element-347">
                    kg
                  </div>
                </div>
              </div>

              <button onClick={handleWeightCapture} className="w-full flex items-center justify-center gap-2 px-6 py-5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors shadow-sm text-lg" data-id="element-348">
                <CheckCircle className="w-6 h-6" strokeWidth={2} data-id="element-349" />
                Gewicht erfassen
              </button>
            </motion.div>}

          {screenState === 'removeItem' && <motion.div key="removeItem" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4
        }} className="w-full max-w-xl space-y-8" data-id="element-350">
              <div className="text-center space-y-4" data-id="element-351">
                <button onClick={() => setShowHistory(true)} className="inline-block px-4 py-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors cursor-pointer" data-id="element-352">
                  <span className="text-sm font-semibold text-blue-700" data-id="element-353">
                    Prüfling {weighedCount + 1} / {totalPruflinge}
                  </span>
                </button>
                <h2 className="text-3xl font-semibold text-gray-900" data-id="element-354">
                  Prüfling von Waage entfernen
                </h2>
                <p className="text-gray-500" data-id="element-355">
                  Entfernen Sie den Prüfling und fahren Sie fort
                </p>
              </div>

              <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-16 shadow-xl overflow-hidden" data-id="element-356">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-357"></div>
                <div className="relative" data-id="element-358">
                  <motion.div initial={{
                scale: 0,
                rotate: -180
              }} animate={{
                scale: 1,
                rotate: 0
              }} transition={{
                duration: 0.5,
                ease: [0.34, 1.56, 0.64, 1]
              }} className="flex items-center justify-center" data-id="element-359">
                    <CheckCircle className="w-24 h-24 text-white" strokeWidth={2} data-id="element-360" />
                  </motion.div>
                </div>
              </div>

              <button onClick={handleNextItem} className="w-full flex items-center justify-center gap-2 px-6 py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm text-lg" data-id="element-361">
                Weiter
              </button>
            </motion.div>}

          {screenState === 'processing' && <motion.div key="processing" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} className="text-center space-y-6" data-id="element-362">
              <div className="text-6xl" data-id="element-363">✓</div>
              <h2 className="text-4xl font-semibold text-gray-900" data-id="element-364">
                Alle Prüflinge erfasst
              </h2>
              <p className="text-gray-500" data-id="element-365">
                {totalPruflinge} von {totalPruflinge} Prüflingen wurden
                erfolgreich geprüft
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {screenState === 'scan' && <button onClick={handleDemo} className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-blue-600 text-gray-700 hover:text-white rounded-full text-sm font-medium transition-colors shadow-sm border border-gray-200 hover:border-blue-600" aria-label="Demo starten" data-id="element-366">
          <Play className="w-4 h-4" strokeWidth={2} data-id="element-367" />
          Demo
        </button>}

      {/* Demo Button für RFID Auth */}
      {showRFIDAuth && !rfidAuthSuccess && <button onClick={handleRFIDAuthDemo} className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-blue-600 text-gray-700 hover:text-white rounded-full text-sm font-medium transition-colors shadow-sm border border-gray-200 hover:border-blue-600 z-[100]" aria-label="RFID Demo" data-id="element-368">
          <Play className="w-4 h-4" strokeWidth={2} data-id="element-369" />
          Demo
        </button>}

      {/* Admin Auth Modal */}
      <AdminAuthModal isVisible={showAdminAuth} onClose={handleAdminAuthClose} onSuccess={handleAdminAuthSuccess} title={adminAuthTarget === 'settings' ? 'Einstellungen - Admin-Zugriff' : 'Stammdaten - Admin-Zugriff'} description="Nur Administratoren haben Zugriff auf diesen Bereich" data-id="element-370" />
    </div>;
}